/**
 * Crop geometry helpers.
 *
 * The on-screen crop rectangle is stored NORMALISED to the video frame
 * ({ x, y, w, h } in 0..1), so it never drifts when the player resizes.
 * It is converted to source pixels only when building the payload sent
 * to the backend ({ x, y, width, height, expectedInputResolution, sourceUrl }).
 */

export const MIN_SIZE = 0.05;
export const CROP_EXIT_MS = 200;

export const ASPECT_PRESETS = [
  { id: 'free', label: 'Free', ratio: null },
  { id: '16:9', label: '16:9', ratio: 16 / 9 },
  { id: '9:16', label: '9:16', ratio: 9 / 16 },
  { id: '1:1', label: '1:1', ratio: 1 },
  { id: '4:5', label: '4:5', ratio: 4 / 5 },
];

export const DEFAULT_SOURCE_SIZE = { width: 1920, height: 1080 };

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
export const clamp01 = (v) => clamp(v, 0, 1);

/** Pixel aspect ratio (w/h) → ratio in normalised frame units. */
export function toNormRatio(ratio, src) {
  if (!ratio || !src?.width || !src?.height) return null;
  return (ratio * src.height) / src.width;
}

/** Where the video picture sits inside a letterboxed/pillarboxed container. */
export function fitFrame(containerW, containerH, srcW, srcH) {
  if (!(containerW > 0) || !(containerH > 0)) return null;
  const ar = srcW > 0 && srcH > 0 ? srcW / srcH : 16 / 9;
  let width;
  let height;
  if (containerW / containerH > ar) {
    height = containerH;
    width = containerH * ar;
  } else {
    width = containerW;
    height = containerW / ar;
  }
  return {
    left: (containerW - width) / 2,
    top: (containerH - height) / 2,
    width,
    height,
  };
}

export function sameBox(a, b) {
  if (!a || !b) return a === b;
  return Math.abs(a.left - b.left) < 0.5
    && Math.abs(a.top - b.top) < 0.5
    && Math.abs(a.width - b.width) < 0.5
    && Math.abs(a.height - b.height) < 0.5;
}

export function clampRect(r) {
  const w = clamp(r.w, 0, 1);
  const h = clamp(r.h, 0, 1);
  return {
    x: clamp(r.x, 0, 1 - w),
    y: clamp(r.y, 0, 1 - h),
    w,
    h,
  };
}

export function centeredRect(normRatio, scale = 0.8) {
  let w = scale;
  let h = scale;
  if (normRatio) {
    h = w / normRatio;
    if (h > scale) {
      h = scale;
      w = h * normRatio;
    }
  }
  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
}

/** Re-shape a rect to an aspect ratio, keeping its centre. */
export function fitRectToRatio(r, normRatio) {
  if (!r || !normRatio) return r;
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  let h = r.h;
  let w = h * normRatio;
  if (w > 1) {
    w = 1;
    h = w / normRatio;
  }
  if (h > 1) {
    h = 1;
    w = h * normRatio;
  }
  return clampRect({ x: cx - w / 2, y: cy - h / 2, w, h });
}

export function moveRect(r, dx, dy) {
  return {
    ...r,
    x: clamp(r.x + dx, 0, 1 - r.w),
    y: clamp(r.y + dy, 0, 1 - r.h),
  };
}

/** Rect spanned by an anchor point and the pointer, optionally ratio-locked. */
export function rectFromPoints(anchor, p, normRatio) {
  const dx = p.x - anchor.x;
  const dy = p.y - anchor.y;
  let w = Math.abs(dx);
  let h = Math.abs(dy);
  if (normRatio) {
    const availW = dx >= 0 ? 1 - anchor.x : anchor.x;
    const availH = dy >= 0 ? 1 - anchor.y : anchor.y;
    w = Math.max(w, h * normRatio);
    h = w / normRatio;
    if (w > availW) {
      w = availW;
      h = w / normRatio;
    }
    if (h > availH) {
      h = availH;
      w = h * normRatio;
    }
  }
  return {
    x: dx >= 0 ? anchor.x : anchor.x - w,
    y: dy >= 0 ? anchor.y : anchor.y - h,
    w,
    h,
  };
}

export function resizeRect(o, handle, p, normRatio) {
  const right = o.x + o.w;
  const bottom = o.y + o.h;

  if (handle.length === 2) {
    const west = handle.includes('w');
    const north = handle.includes('n');
    const anchor = { x: west ? right : o.x, y: north ? bottom : o.y };
    const px = west ? Math.min(p.x, anchor.x - MIN_SIZE) : Math.max(p.x, anchor.x + MIN_SIZE);
    const py = north ? Math.min(p.y, anchor.y - MIN_SIZE) : Math.max(p.y, anchor.y + MIN_SIZE);
    const next = rectFromPoints(anchor, { x: clamp01(px), y: clamp01(py) }, normRatio);
    return next.w >= MIN_SIZE / 2 && next.h >= MIN_SIZE / 2 ? next : o;
  }

  let { x, y, w, h } = o;
  if (handle === 'n') {
    y = Math.min(p.y, bottom - MIN_SIZE);
    h = bottom - y;
  } else if (handle === 's') {
    h = Math.max(p.y, o.y + MIN_SIZE) - o.y;
  } else if (handle === 'w') {
    x = Math.min(p.x, right - MIN_SIZE);
    w = right - x;
  } else if (handle === 'e') {
    w = Math.max(p.x, o.x + MIN_SIZE) - o.x;
  }
  return clampRect({ x, y, w, h });
}

/** Normalised rect → backend crop payload in source pixels (even sizes for ffmpeg). */
export function rectToCrop(r, src, sourceUrl) {
  if (!r) return null;
  const W = src?.width || DEFAULT_SOURCE_SIZE.width;
  const H = src?.height || DEFAULT_SOURCE_SIZE.height;

  let x = Math.round(r.x * W);
  let y = Math.round(r.y * H);
  let width = Math.round(r.w * W);
  let height = Math.round(r.h * H);

  x = clamp(x, 0, W - 2);
  y = clamp(y, 0, H - 2);
  width = Math.min(width, W - x);
  height = Math.min(height, H - y);
  width -= width % 2;
  height -= height % 2;

  if (width < 2 || height < 2) return null;

  return {
    x,
    y,
    width,
    height,
    expectedInputResolution: { width: W, height: H },
    sourceUrl,
  };
}

/** Saved crop payload → normalised rect (so an existing crop can be edited). */
export function cropToRect(crop, src) {
  if (!crop || !(crop.width > 0) || !(crop.height > 0)) return null;
  const W = crop.expectedInputResolution?.width || src?.width || DEFAULT_SOURCE_SIZE.width;
  const H = crop.expectedInputResolution?.height || src?.height || DEFAULT_SOURCE_SIZE.height;
  return clampRect({
    x: (crop.x || 0) / W,
    y: (crop.y || 0) / H,
    w: crop.width / W,
    h: crop.height / H,
  });
}
